// ============================================================================
// BP-04-02: Documents Frontend - useDocuments Hook
// ============================================================================

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { documentsApi, bulkApi, statsApi } from '../api/documentsApi';
import { useDocumentsStore } from '../stores/documentsStore';
import type {
  Document,
  DocumentCreate,
  DocumentUpdate,
  DocumentSearchParams,
  SortField,
  SortOrder,
} from '../types/documentsTypes';

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (familyId: string, params?: DocumentSearchParams) =>
    [...documentKeys.lists(), familyId, params] as const,
  infiniteList: (familyId: string, params?: Omit<DocumentSearchParams, 'page'>) =>
    [...documentKeys.lists(), 'infinite', familyId, params] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
  stats: (familyId: string) => [...documentKeys.all, 'stats', familyId] as const,
};

// -----------------------------------------------------------------------------
// List Documents Hook
// -----------------------------------------------------------------------------

interface UseDocumentsOptions {
  familyId: string;
  enabled?: boolean;
  params?: DocumentSearchParams;
}

export function useDocuments({ familyId, enabled = true, params }: UseDocumentsOptions) {
  const { filters, sortBy, sortOrder, currentFolderId } = useDocumentsStore();

  const queryParams: DocumentSearchParams = {
    ...params,
    ...filters,
    folder_id: currentFolderId,
    sort_by: sortBy,
    sort_order: sortOrder,
  };

  return useQuery({
    queryKey: documentKeys.list(familyId, queryParams),
    queryFn: () => documentsApi.list(familyId, queryParams),
    enabled: enabled && !!familyId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// -----------------------------------------------------------------------------
// Infinite Documents Hook (for pagination)
// -----------------------------------------------------------------------------

export function useInfiniteDocuments({ familyId, enabled = true, params }: UseDocumentsOptions) {
  const { filters, sortBy, sortOrder, currentFolderId } = useDocumentsStore();

  const queryParams: Omit<DocumentSearchParams, 'page'> = {
    ...params,
    ...filters,
    folder_id: currentFolderId,
    sort_by: sortBy,
    sort_order: sortOrder,
    per_page: 20,
  };

  return useInfiniteQuery({
    queryKey: documentKeys.infiniteList(familyId, queryParams),
    queryFn: ({ pageParam = 1 }) =>
      documentsApi.list(familyId, { ...queryParams, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
    enabled: enabled && !!familyId,
    staleTime: 30 * 1000,
  });
}

// -----------------------------------------------------------------------------
// Single Document Hook
// -----------------------------------------------------------------------------

export function useDocument(documentId: string, enabled = true) {
  const { addToRecentDocuments } = useDocumentsStore();

  return useQuery({
    queryKey: documentKeys.detail(documentId),
    queryFn: async () => {
      const document = await documentsApi.get(documentId);
      addToRecentDocuments(document);
      return document;
    },
    enabled: enabled && !!documentId,
    staleTime: 60 * 1000, // 1 minute
  });
}

// -----------------------------------------------------------------------------
// Document Mutations
// -----------------------------------------------------------------------------

export function useCreateDocument(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      metadata,
      onProgress,
    }: {
      file: File;
      metadata: DocumentCreate;
      onProgress?: (progress: number) => void;
    }) => documentsApi.upload(familyId, file, metadata, onProgress),
    onSuccess: (newDocument) => {
      // Invalidate document lists
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      // Add to cache
      queryClient.setQueryData(documentKeys.detail(newDocument.id), newDocument);
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: documentKeys.stats(familyId) });
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, data }: { documentId: string; data: DocumentUpdate }) =>
      documentsApi.update(documentId, data),
    onSuccess: (updatedDocument) => {
      queryClient.setQueryData(documentKeys.detail(updatedDocument.id), updatedDocument);
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

export function useDeleteDocument(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => documentsApi.delete(documentId),
    onSuccess: (_, documentId) => {
      queryClient.removeQueries({ queryKey: documentKeys.detail(documentId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentKeys.stats(familyId) });
    },
  });
}

export function useMoveDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      targetFolderId,
    }: {
      documentId: string;
      targetFolderId: string | null;
    }) => documentsApi.move(documentId, targetFolderId),
    onSuccess: (updatedDocument) => {
      queryClient.setQueryData(documentKeys.detail(updatedDocument.id), updatedDocument);
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

export function useCopyDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      targetFolderId,
    }: {
      documentId: string;
      targetFolderId: string | null;
    }) => documentsApi.copy(documentId, targetFolderId),
    onSuccess: (newDocument) => {
      queryClient.setQueryData(documentKeys.detail(newDocument.id), newDocument);
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

// -----------------------------------------------------------------------------
// Bulk Operations
// -----------------------------------------------------------------------------

export function useBulkMoveDocuments(familyId: string) {
  const queryClient = useQueryClient();
  const { clearSelection } = useDocumentsStore();

  return useMutation({
    mutationFn: ({
      documentIds,
      targetFolderId,
    }: {
      documentIds: string[];
      targetFolderId: string | null;
    }) => bulkApi.move(familyId, { document_ids: documentIds, target_folder_id: targetFolderId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      clearSelection();
    },
  });
}

export function useBulkDeleteDocuments(familyId: string) {
  const queryClient = useQueryClient();
  const { clearSelection } = useDocumentsStore();

  return useMutation({
    mutationFn: (documentIds: string[]) =>
      bulkApi.delete(familyId, { document_ids: documentIds }),
    onSuccess: (_, documentIds) => {
      documentIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: documentKeys.detail(id) });
      });
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentKeys.stats(familyId) });
      clearSelection();
    },
  });
}

export function useBulkDownloadDocuments(familyId: string) {
  return useMutation({
    mutationFn: (documentIds: string[]) =>
      bulkApi.download(familyId, { document_ids: documentIds }),
    onSuccess: (result) => {
      // Open download URL in new tab
      window.open(result.download_url, '_blank');
    },
  });
}

// -----------------------------------------------------------------------------
// Document URLs
// -----------------------------------------------------------------------------

export function useDocumentDownloadUrl(documentId: string, enabled = false) {
  return useQuery({
    queryKey: [...documentKeys.detail(documentId), 'download'],
    queryFn: () => documentsApi.getDownloadUrl(documentId),
    enabled: enabled && !!documentId,
    staleTime: 5 * 60 * 1000, // 5 minutes (URLs typically expire after 15)
  });
}

export function useDocumentPreviewUrl(documentId: string, enabled = false) {
  return useQuery({
    queryKey: [...documentKeys.detail(documentId), 'preview'],
    queryFn: () => documentsApi.getPreviewUrl(documentId),
    enabled: enabled && !!documentId,
    staleTime: 5 * 60 * 1000,
  });
}

// -----------------------------------------------------------------------------
// Statistics Hook
// -----------------------------------------------------------------------------

export function useDocumentStats(familyId: string, enabled = true) {
  return useQuery({
    queryKey: documentKeys.stats(familyId),
    queryFn: () => statsApi.get(familyId),
    enabled: enabled && !!familyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// -----------------------------------------------------------------------------
// Export All
// -----------------------------------------------------------------------------





